import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useDispatch } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Stack,
  MenuItem,
  Typography,
  CircularProgress,
  useTheme,
  Dialog,
} from "@mui/material";
import { Close as CloseIcon, WarningAmber as WarningAmberIcon } from "@mui/icons-material";
import { updateExistingHoliday } from "store/features/company/leaveManagement/holiday/holidayActions";
import editHolidaySvg from "assets/modals/edit.svg";

const EditHolidayModal = ({ isOpen, onClose, holiday, onSuccess, holidays = [] }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showSameDayWarningModal, setShowSameDayWarningModal] = useState(false);
  const [formData, setFormData] = useState({
    holiday_name: "",
    holiday_date: "",
    holiday_type: "public",
    description: "",
  });

  const normalizeDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const existingHoliday = useMemo(() => {
    if (!formData.holiday_date || !holidays || holidays.length === 0) return null;
    return holidays.find(
      (h) => h.id !== holiday?.id && normalizeDate(h.holiday_date) === formData.holiday_date
    );
  }, [formData.holiday_date, holidays, holiday?.id]);

  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      const originalPaddingRight = document.body.style.paddingRight;

      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;

      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;

      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (holiday) {
      const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      setFormData({
        holiday_name: holiday.holiday_name || "",
        holiday_date: formatDateForInput(holiday.holiday_date),
        holiday_type: holiday.holiday_type || "public",
        description: holiday.description || "",
      });
      setShowSameDayWarningModal(false);
    }
  }, [holiday]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (name, value) => {
    const formattedValue = value && dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : "";
    setFormData((prev) => ({ ...prev, [name]: formattedValue }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (existingHoliday) {
      setShowSameDayWarningModal(true);
    } else {
      executeUpdate();
    }
  };

  const executeUpdate = async () => {
    setShowSameDayWarningModal(false);
    setLoading(true);

    const result = await dispatch(updateExistingHoliday(holiday.id, formData));

    if (result.success) {
      onSuccess();
      onClose();
    }
    setLoading(false);
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#f59e0b",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: "text.secondary",
      position: "relative",
      transform: "none",
      marginBottom: "8px",
      display: "block",
      "&.Mui-focused": {
        color: "primary.main",
      },
      "& .MuiInputLabel-asterisk": {
        color: "#ef4444",
      },
    },
  };

  return (
    <AnimatePresence>
      {isOpen && holiday && (
        <>
          <style>
            {`
          .MuiPopover-root {
            z-index: 1400 !important;
          }
          .MuiPopper-root {
            z-index: 1400 !important;
          }
        `}
          </style>

          {/* Backdrop with fade animation */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(252, 226, 181, 0.14)"
                  : "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            }}
          />

          {/* Modal with slide-in from right animation - Fixed overflow */}
          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: 1301,
              height: "100vh",
              width: "100%",
              maxWidth: "600px",
              overflowY: "auto",
              overflowX: "hidden",
              backgroundColor:
                theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              boxShadow: theme.shadows[24],
            }}
          >
            {/* Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                borderBottom: `1px solid ${theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                  }`,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              }}
            >
              <Box sx={{ p: 3, pr: 7 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box flexShrink={0}>
                    <Box
                      component="img"
                      src={editHolidaySvg}
                      alt="Edit Holiday"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                        transition: "all 0.3s",
                        "&:hover": {
                          filter: "drop-shadow(0 4px 8px rgba(245,158,11,0.3))",
                        },
                      }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontWeight: "bold",
                        color: "text.primary",
                      }}
                    >
                      Edit Holiday
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color: "text.secondary",
                      }}
                    >
                      Update holiday information
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  color: "text.secondary",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f59e0b",
                    color: "#ffffff",
                    transform: "scale(1.05)",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Form - Added scrollbarGutter to prevent shift */}
            <form onSubmit={handleSubmit}>
              <Box
                sx={{
                  p: 3,
                  scrollbarGutter: "stable",
                }}
              >
                <Stack spacing={3}>
                  {/* Holiday Name */}
                  <TextField
                    fullWidth
                    label="Holiday Name"
                    name="holiday_name"
                    value={formData.holiday_name}
                    onChange={handleChange}
                    required
                    sx={inputSx}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />

                  {/* Holiday Date and Type Row */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <DatePicker
                      label="Holiday Date"
                      value={formData.holiday_date ? dayjs(formData.holiday_date) : null}
                      onChange={(newValue) => handleDateChange("holiday_date", newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          sx: inputSx,
                          InputLabelProps: { shrink: true }
                        }
                      }}
                    />

                    <TextField
                      fullWidth
                      select
                      label="Holiday Type"
                      name="holiday_type"
                      value={formData.holiday_type}
                      onChange={handleChange}
                      required
                      helperText="Select the type of holiday"
                      sx={inputSx}
                      SelectProps={{
                        MenuProps: {
                          sx: { zIndex: 1400 },
                          PaperProps: {
                            sx: { zIndex: 1400 },
                          },
                          disableScrollLock: true,
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    >
                      <MenuItem value="public">Public Holiday</MenuItem>
                      <MenuItem value="company">Company Holiday</MenuItem>
                    </TextField>
                  </Stack>

                  {/* Description */}
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    sx={inputSx}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />

                  {/* Same Day Existing Holiday Warning Alert */}
                  {existingHoliday && (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: "12px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(245, 158, 11, 0.15)"
                            : "#fffbe6",
                        border: `1px solid ${
                          theme.palette.mode === "dark" ? "#b45309" : "#ffe58f"
                        }`,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                      }}
                    >
                      <WarningAmberIcon sx={{ color: "primary.main", mt: 0.2, flexShrink: 0 }} />
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.mode === "dark" ? "#fef3c7" : "#d97706",
                          }}
                        >
                          Same Day Holiday Warning
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.mode === "dark" ? "#cbd5e1" : "#4b5563",
                            mt: 0.5,
                          }}
                        >
                          A holiday named <strong>"{existingHoliday.holiday_name}"</strong> (
                          {existingHoliday.holiday_type === "public"
                            ? "Public Holiday"
                            : "Company Holiday"}
                          ) is already scheduled for this date.
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Stack>
              </Box>

              {/* Buttons */}
              <Box
                sx={{
                  p: 3,
                  borderTop: `1px solid ${theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                    }`,
                  display: "flex",
                  gap: 2,
                }}
              >
                <Button
                  onClick={onClose}
                  disabled={loading}
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    borderColor:
                      theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                    color:
                      theme.palette.mode === "dark" ? "#d1d5db" : "#374151",
                    "&:hover": {
                      borderColor: "#f59e0b",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(245,158,11,0.1)"
                          : "#fffbeb",
                    },
                  }}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    color: "#ffffff",
                    boxShadow: "0 10px 15px -3px rgba(245, 158, 11, 0.25)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                      transform: "scale(1.02)",
                      boxShadow: "0 20px 25px -5px rgba(245, 158, 11, 0.3)",
                    },
                    "&:disabled": {
                      opacity: 0.7,
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                  ) : (
                    "Update Holiday"
                  )}
                </Button>
              </Box>
            </form>
          </motion.div>

          {/* Same Day Warning Confirmation Modal */}
          <Dialog
            open={showSameDayWarningModal}
            onClose={() => setShowSameDayWarningModal(false)}
            sx={{
              zIndex: 1500,
              "& .MuiDialog-paper": {
                borderRadius: "16px",
                p: 1,
                backgroundColor: theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
                maxWidth: "440px",
              },
            }}
          >
            <Box sx={{ p: 2.5, textAlign: "center" }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.mode === "dark" ? "rgba(245,158,11,0.2)" : "#fef3c7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <WarningAmberIcon sx={{ color: "primary.main", fontSize: 32 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}>
                Existing Holiday Conflict
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
                A holiday named <strong>"{existingHoliday?.holiday_name}"</strong> already exists on <strong>{formData.holiday_date}</strong>. Are you sure you want to update this holiday to the same date?
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setShowSameDayWarningModal(false)}
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                    color: theme.palette.mode === "dark" ? "#d1d5db" : "#374151",
                    borderColor: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                  }}
                >
                  Change Date
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={executeUpdate}
                  disabled={loading}
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                    background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    color: "#ffffff",
                  }}
                >
                  {loading ? <CircularProgress size={20} sx={{ color: "#ffffff" }} /> : "Proceed & Update"}
                </Button>
              </Stack>
            </Box>
          </Dialog>
        </>
      )}
    </AnimatePresence>
  );
};

export default EditHolidayModal;
